import React, { FC } from 'react';
import { PNGFile } from './FileAnimation';
import { Close, Refresh, TrashOutline } from 'react-ionicons';
import clsx from 'clsx';
import ProgressIndicator from './ProgressIndicator';

interface AttachmentProps {
  file: File;
  onCancel: () => void;
  status: 'Processing' | 'Failed' | 'Success';
  percentage: number;
  onRepeat: () => void;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const Attachment: FC<AttachmentProps> = ({
  file,
  onCancel,
  percentage,
  status,
  onRepeat,
}) => {
  return (
    <div className="space-y-4 rounded bg-neutral-500 bg-opacity-[8%] p-4 shadow-[0px_4px_8px_0px_rgba(0,_0,_0,_0.16)]">
      <div className="flex gap-4">
        <div className="self-center">
          <PNGFile />
        </div>
        <div className="flex w-[150px] flex-col gap-1 sm:w-[344px]">
          <p className="truncate text-sm font-semibold text-content-base-default">
            {file.name}
          </p>
          <div className="flex gap-2 text-xs font-semibold text-content-base-subdued">
            <p>{formatFileSize(file.size)}</p>
            <div className="h-full w-[1px] bg-stroke-base-subdued py-0.5" />
            <p
              className={clsx({
                'text-red-400': status === 'Failed',
                'text-green-300': status === 'Success',
              })}
            >
              {status}
            </p>
          </div>
        </div>
        <div
          className={clsx('text-sm font-semibold text-content-base-default', {
            'text-red-400': status === 'Failed',
            'text-green-300': status === 'Success',
          })}
        >
          {percentage}%
        </div>
        {status === 'Processing' && (
          <div className="text-content-base-default" onClick={onCancel}>
            <Close width="20px" height="20px" color="currentColor" />
          </div>
        )}
        {status === 'Failed' && (
          <div className="text-content-base-default" onClick={onRepeat}>
            <Refresh width="20px" height="20px" color="currentColor" />
          </div>
        )}
        {status === 'Success' && (
          <div className="text-content-base-default" onClick={onCancel}>
            <TrashOutline width="20px" height="20px" color="currentColor" />
          </div>
        )}
      </div>
      <ProgressIndicator
        percentage={percentage}
        barClassName={clsx({
          'bg-teal-400': status === 'Processing',
          'bg-red-400': status === 'Failed',
          'bg-green-300': status === 'Success',
        })}
      />
    </div>
  );
};

export default Attachment;
